import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ChristmasVoucherBanner } from "@/components/vouchers/ChristmasVoucherBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { buyer_name: "", buyer_email: "", buyer_phone: "", recipient_name: "", message: "" };
const FIELDS = [["buyer_name", "Your name", "text"], ["buyer_email", "Your email (gift card is sent here)", "email"], ["buyer_phone", "Your mobile", "tel"], ["recipient_name", "Who's it for?", "text"]];

const Option = ({ opt, active, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`gift-option-${opt.id}`}
    className={`p-4 rounded-xl border text-left transition-colors ${active ? "border-amber-300 bg-amber-300/15" : "border-dream-border bg-dream-bg hover:border-white/40"}`}>
    <p className="text-white font-semibold text-sm leading-snug">{opt.label}</p>
    <p className={`font-mono mt-1 ${active ? "text-amber-300" : "text-white/60"}`}>${opt.value.toFixed(opt.value % 1 ? 2 : 0)}</p>
  </button>
);

const Preview = ({ opt, recipient }) => (
  <div className="rotate-[-2deg] rounded-3xl p-7 bg-gradient-to-br from-[#7f1d1d] to-black border border-amber-300/40 shadow-2xl max-w-sm" data-testid="gift-preview">
    <p className="text-amber-300 text-[10px] font-bold tracking-[0.3em]">DREAM MAKERS RECORDING GROUP</p>
    <p className="font-heading text-2xl font-bold text-white mt-2">Gift Card</p>
    <p className="text-white/70 text-sm">For {recipient || "someone special"}</p>
    <p className="text-white/90 text-sm mt-3">{opt?.service_id ? opt.label : "Spend on any service"}</p>
    <p className="font-heading text-5xl font-extrabold text-amber-300 mt-1" data-testid="gift-preview-value">${opt ? opt.value.toFixed(opt.value % 1 ? 2 : 0) : "0"}</p>
    <p className="mt-3 font-mono text-sm tracking-widest text-white/40">DMRG-XXXX-XXXX</p>
  </div>
);

export default function GiftVouchers() {
  const [options, setOptions] = useState([]);
  const [mode, setMode] = useState("service");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/vouchers/options`).then((r) => {
      setOptions(r.data.options);
      setSelected(r.data.options.find((o) => o.service_id)?.id);
    });
  }, []);

  const list = options.filter((o) => (mode === "service" ? o.service_id : !o.service_id));
  const opt = options.find((o) => o.id === selected);
  const switchMode = (m) => { setMode(m); setSelected(options.find((o) => (m === "service" ? o.service_id : !o.service_id))?.id); };

  const buy = async () => {
    if (!opt) return toast.error("Please choose a gift card");
    if (FIELDS.some(([k]) => !form[k].trim()) || !/^\S+@\S+\.\S+$/.test(form.buyer_email)) return toast.error("Please fill in your details");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/vouchers/purchase`, { option: opt.id, scope: "all", ...form });
      const c = await axios.post(`${API}/checkout/create`, { booking_id: r.data.booking_id, origin_url: window.location.origin });
      window.location.href = c.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout, please try again");
      setLoading(false);
    }
  };

  return (
    <div className="pt-36 min-h-screen bg-dream-bg" data-testid="gift-vouchers-page">
      <ChristmasVoucherBanner scope="all" />
      <section className="max-w-6xl mx-auto px-6 py-16 md:py-24 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
        <div>
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-300 text-black text-xs font-bold uppercase tracking-widest"><Gift className="w-4 h-4" /> Gift cards</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[0.95] mt-5">Give them<br />studio time.</h1>
          <p className="text-white/65 mt-5 leading-relaxed max-w-md">Gift a specific session or a dollar amount they can spend on anything at Dream Makers. Emailed to you instantly as a printable gift card with a unique code, valid for 6 months.</p>
          <div className="mt-10"><Preview opt={opt} recipient={form.recipient_name} /></div>
        </div>
        <div className="rounded-3xl p-6 md:p-8 bg-dream-surface border border-dream-border space-y-6">
          <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-dream-bg border border-dream-border">
            {[["service", "Pick a service"], ["amount", "Choose an amount"]].map(([m, l]) => (
              <button key={m} type="button" onClick={() => switchMode(m)} data-testid={`gift-mode-${m}`}
                className={`rounded-full py-2.5 text-sm font-semibold transition-colors ${mode === m ? "bg-amber-300 text-black" : "text-white/60 hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <div className={`grid gap-3 ${mode === "service" ? "sm:grid-cols-2" : "grid-cols-3"}`} data-testid="gift-options">
            {list.map((o) => <Option key={o.id} opt={o} active={selected === o.id} onClick={() => setSelected(o.id)} />)}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, type]) => (
              <label key={k} className="block">
                <span className="text-white/60 text-sm">{label}</span>
                <Input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-1 bg-dream-bg border-dream-border text-white" data-testid={`gift-${k}`} />
              </label>
            ))}
          </div>
          <label className="block">
            <span className="text-white/60 text-sm">Personal message (optional)</span>
            <Textarea rows={2} maxLength={300} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Merry Christmas! Go make something amazing." className="mt-1 bg-dream-bg border-dream-border text-white" data-testid="gift-message" />
          </label>
          <Button onClick={buy} disabled={loading || !opt} className="w-full bg-amber-300 hover:bg-white text-black rounded-full py-7 text-base font-bold" data-testid="gift-buy-btn">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Buy $${opt ? opt.value.toFixed(opt.value % 1 ? 2 : 0) : ""} Gift Card`}
          </Button>
          <p className="text-white/45 text-xs text-center">Afterpay &amp; Zip available at checkout · Valid 6 months · Redeem at checkout on the Book Now page</p>
        </div>
      </section>
    </div>
  );
}
